// ANGEL FLIGHT — GameLoop: the engine-agnostic mission orchestrator.
//
// Extracted from the old Phaser GameScene. Owns the live-enemy array + the
// gunner, assembles the GameContext, builds the systems in a fixed order, ticks
// them against a capped dt (SIM frozen during hitstop), and resolves mission
// win/lose (banks rewards, persists, emits a result). Renderer-agnostic: it is
// handed a GameView + a WorldRenderer, so it runs headless in tests with stubs.

import { newCombo } from '../config/economy';
import { scoreMission } from '../config/grading';
import { getDifficulty } from '../config/difficulty';
import { resolvePlayerStats } from '../config/upgrades';
import { createEventBus, type TypedEmitter } from '../core/EventBus';
import { createRng } from '../core/rng';
import { writeSave } from '../core/storage';
import type { MissionResult, SaveStateV1 } from '../core/types';
import { Gunner } from '../entities/Gunner';
import { getMission, missionIndexOf } from '../missions/index';
import { Platform } from '../platform';
import { rewardMissionClear } from '../progression';
import { EconomySystem } from '../systems/EconomySystem';
import { EnemySystem } from '../systems/EnemySystem';
import { FiringSystem } from '../systems/FiringSystem';
import { InputSystem } from '../systems/InputSystem';
import { ObjectiveSystem } from '../systems/ObjectiveSystem';
import type { GameContext, GameSystem, LiveEnemy, RunState } from '../systems/System';
import type { GameView } from '../render/GameView';

/** What the loop needs from the world renderer (Stage 2 WorldView3D implements
 *  this; a no-op stub satisfies it headlessly). The renderer is a GameSystem
 *  (init/update/destroy) plus a per-enemy flash hook the firing system drives. */
export interface WorldRenderer extends GameSystem {
  flash(id: number, ms: number): void;
}

const noopRenderer: WorldRenderer = {
  init() {},
  update() {},
  destroy() {},
  flash() {},
};

export interface GameLoopOptions {
  missionId: string;
  save: SaveStateV1;
  view: GameView;
  /** The depth-sorted horde renderer. Omit (or pass the stub) for headless runs. */
  worldRenderer?: WorldRenderer;
  /** Extra render-only systems (gun view, camera rig, juice) appended after the
   *  sim + world renderer. Always tick; never frozen-gated. */
  renderSystems?: GameSystem[];
  /** Hitstop gate: returns true while the sim should freeze (the CameraRig owns
   *  this in-game; headless runs omit it → never frozen). */
  isFrozen?: () => boolean;
  /** Called once when the mission resolves, with the banked result. */
  onResolved?: (result: MissionResult, cleared: boolean) => void;
}

export class GameLoop {
  readonly bus: TypedEmitter;
  readonly ctx: GameContext;
  readonly gunner: Gunner;
  private readonly enemies: LiveEnemy[] = [];
  private readonly simSystems: GameSystem[] = [];
  private readonly renderSystems: GameSystem[] = [];
  private readonly worldRenderer: WorldRenderer;
  private readonly isFrozen: () => boolean;
  private readonly save: SaveStateV1;
  private readonly firing: FiringSystem;
  private readonly input: InputSystem;
  private readonly onResolved?: (result: MissionResult, cleared: boolean) => void;
  private resolved = false;

  /** Intro-cinematic gate: while true, ONLY the render systems tick (the world
   *  keeps scrolling/banking) — no sim systems, no gunner timers, no elapsed
   *  time, no input. App flips it false at the cinematic hand-off. */
  introHold = false;

  constructor(opts: GameLoopOptions) {
    this.bus = createEventBus();
    this.save = opts.save;
    this.onResolved = opts.onResolved;
    this.isFrozen = opts.isFrozen ?? (() => false);
    this.worldRenderer = opts.worldRenderer ?? noopRenderer;

    const mission = getMission(opts.missionId);
    const diff = getDifficulty(opts.save.difficulty);
    const stats = resolvePlayerStats(opts.save, diff);
    this.gunner = new Gunner(stats.maxHull, stats.autoRepair);
    // Deploy with the EQUIPPED weapon (the dead save field comes alive). The
    // storage migration guarantees equippedWeapon ∈ ownedWeapons; re-check
    // anyway so a hand-edited save can't deploy an unowned gun.
    if (opts.save.ownedWeapons.includes(opts.save.equippedWeapon)) {
      this.gunner.activeWeapon = opts.save.equippedWeapon;
    }

    const run: RunState = {
      kills: 0,
      scrip: 0,
      salvage: 0,
      bestCombo: 0,
      headshots: 0,
      elapsedMs: 0,
      friendliesHit: 0,
      shotsFired: 0,
      shotsHit: 0,
      combo: newCombo(),
    };

    // TWO independent rng streams off the same mission seed: the SIM stream
    // (spread jitter, spawn lanes — deterministic outcomes) and the RENDER
    // stream (shake, prop tints/placement). Render draws must NEVER advance the
    // sim stream, or sim outcomes become frame-rate-dependent (the seam is
    // pinned by GameLoop.determinism.test.ts).
    const seed = (opts.missionId.length * 2654435761) >>> 0;
    this.ctx = {
      view: opts.view,
      bus: this.bus,
      rng: createRng(seed),
      renderRng: createRng((seed ^ 0x9e3779b9) >>> 0),
      difficulty: opts.save.difficulty,
      stats,
      ownedWeapons: [...opts.save.ownedWeapons],
      mission,
      run,
      enemies: this.enemies,
      gunner: this.gunner,
    };

    // SIM systems (frozen during hitstop) in fixed order.
    const enemySystem = new EnemySystem();
    const firing = new FiringSystem();
    this.firing = firing;
    this.input = new InputSystem();
    this.simSystems = [
      this.input,
      firing,
      enemySystem,
      new EconomySystem(),
      new ObjectiveSystem(enemySystem),
    ];
    // Render systems (always tick): the horde renderer first, then any extras.
    this.renderSystems = [this.worldRenderer, ...(opts.renderSystems ?? [])];

    for (const s of this.simSystems) s.init(this.ctx);
    for (const s of this.renderSystems) s.init(this.ctx);

    // Let the firing system flash enemies through the world renderer.
    firing.flashEnemy = (id, ms) => this.worldRenderer.flash(id, ms);

    this.bus.on('mission:cleared', () => this.endMission(true));
    this.bus.on('mission:failed', () => this.endMission(false));
  }

  /** Re-announce the live weapon + ammo state on the bus. The HUD binds AFTER
   *  the systems init (the bus is born inside this constructor), so the init
   *  emissions are gone by then — App calls this right after hud.bind so the
   *  ammo readout + weapon name are honest BEFORE the first shot. */
  announceHud(): void {
    this.firing.announce();
  }

  /** Pop countermeasure flares — the touch FLARE button's path into the sim
   *  (key F reaches the same InputSystem.deployFlares directly). A no-op while
   *  on cooldown; the InputSystem emits `flare:deployed` when it fires. */
  deployFlares(): void {
    this.input.deployFlares();
  }

  /** Advance one frame. `delta` ms is capped at 50; SIM freezes during hitstop. */
  update(delta: number): void {
    if (this.resolved) return;
    const dt = Math.min(delta, 50);

    if (this.introHold) {
      // Cinematic: presentation only. The world renderer + camera rig + juice
      // tick (scroll/bank stays alive); the entire sim stands still.
      this.ctx.simFrozen = false; // the flyover SHOULD keep scrolling
      for (const s of this.renderSystems) s.update(dt);
      return;
    }

    this.gunner.update(dt);
    const frozen = this.isFrozen();
    // Publish the freeze to the render layer: sim-coupled motion (the ground/
    // prop scroll that mirrors worldDriftMps) must hold still WITH the enemy
    // positions, or every hitstop slides the horde backward over the asphalt.
    this.ctx.simFrozen = frozen;
    if (!frozen) {
      for (const s of this.simSystems) s.update(dt);
      this.ctx.run.elapsedMs += dt;
    } else {
      // input still tracks the finger during hitstop (it's simSystems[0]).
      this.simSystems[0]?.update(dt);
    }
    for (const s of this.renderSystems) s.update(dt);
  }

  get isResolved(): boolean {
    return this.resolved;
  }

  private endMission(cleared: boolean): void {
    if (this.resolved) return;
    this.resolved = true;
    const run = this.ctx.run;
    const missionId = this.ctx.mission.id;

    // The graded score (config/grading.ts is the SINGLE score formula). Compute
    // it from a result-shaped snapshot so the loop and the Debrief never drift.
    const stats = {
      kills: run.kills,
      scrip: run.scrip,
      salvage: run.salvage,
      bestCombo: run.bestCombo,
      headshots: run.headshots,
      elapsedMs: run.elapsedMs,
      friendliesHit: run.friendliesHit,
      shotsFired: run.shotsFired,
      shotsHit: run.shotsHit,
    };

    // ENDLESS (Last Stand) banks its best on the DOWNED run (that's the natural
    // end of the mode, not a wipe); survive/boss only bank on a clear.
    const objective = this.ctx.mission.objective;
    const endless = objective === 'endless';
    const banks = cleared || endless;

    // Capture the prior best BEFORE banking so NEW BEST is honest.
    const prevBest = this.save.bestScores[missionId] ?? 0;
    const score = scoreMission({ missionId, cleared, stats, score: 0, newBest: false, prevBest, objective });
    const newBest = banks && score > prevBest;

    this.save.scrip += run.scrip;
    this.save.salvage += run.salvage;
    // Persist the gun the player ended the run holding — the next deploy starts
    // on it (equippedWeapon stays alive across sessions, never an unowned gun).
    if (this.save.ownedWeapons.includes(this.gunner.activeWeapon)) {
      this.save.equippedWeapon = this.gunner.activeWeapon;
    }
    if (cleared) {
      // Progress = the FURTHEST mission cleared, never a replay counter: a
      // re-clear of M1 must not mark M2 cleared (max, not +=). Unknown ids
      // (?dev typos resolve to M1 anyway) leave progress untouched. Endless is
      // NOT in the campaign (missionIndexOf → -1), so it never advances progress.
      const idx = missionIndexOf(missionId);
      if (idx >= 0) {
        this.save.missionProgress = Math.max(this.save.missionProgress, idx + 1);
        // Shared-economy FIRST-CLEAR payout: pay creditsPerClear once per campaign
        // mission (earnOnce makes re-clears no-ops). Genuine WIN only — this whole
        // branch is gated on `cleared`. Event id 'gunner:clear:<idx>' (integer tail
        // the server requires). No-op when unlockAll/anon.
        rewardMissionClear(idx);
      }
    }
    // ENDLESS first-survival payout: the natural end of Last Stand is the bird
    // going DOWN (cleared:false, endless:true), so its payout sits OUTSIDE the
    // cleared branch. earnOnce('gunner:clear:4') pays once ever (the slot past the
    // four campaign missions; integer tail, as the server requires).
    if (endless && banks) {
      Platform.earnOnce('gunner:clear:4', Platform.limits.creditsPerClear);
    }
    // Bank the high score for any banking run (cleared campaign OR an endless
    // last stand) — keyed by mode id, so endless keeps its own leaderboard.
    if (banks) {
      this.save.bestScores[missionId] = Math.max(prevBest, score);
    }
    writeSave(this.save);
    this.bus.emit('save:written', {});

    const result: MissionResult = { missionId, cleared, objective, stats, score, newBest, prevBest };
    this.onResolved?.(result, cleared);
  }

  /** Test handles mirroring the old GameScene.testHandles (Stage 5 smoke port). */
  get testHandles(): {
    gunner: Gunner;
    ctx: GameContext;
    enemyCount: () => number;
    setFiring: (on: boolean) => void;
    aimAt: (x: number, y: number) => void;
    isResolved: () => boolean;
  } {
    return {
      gunner: this.gunner,
      ctx: this.ctx,
      enemyCount: () => this.enemies.length,
      setFiring: (on) => {
        this.gunner.firing = on;
        this.gunner.spinup = on ? 1 : 0;
      },
      aimAt: (x, y) => {
        this.gunner.aim = { x, y };
      },
      isResolved: () => this.resolved,
    };
  }

  destroy(): void {
    for (const s of this.simSystems) s.destroy();
    for (const s of this.renderSystems) s.destroy();
    this.enemies.length = 0;
    this.bus.removeAll();
  }
}
